<script lang="ts" setup>
import type { Editor } from '@tiptap/vue-3'
import { computed } from 'vue'

import { ActionDropdownButtonSplit, type EditorIconType } from '@/components/editor'
import { ColumnLayoutEnum } from '@/components/editor/extensions/MultiColumn'
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider, DropdownMenuItem, DropdownMenuSeparator } from '@/ui'

interface ColumnLayoutOption {
  label: string
  value: ColumnLayoutEnum
  icon: 'two-columns' | 'sidebar-left' | 'sidebar-right'
}

interface Props {
  editor: Editor
  disabled?: boolean
  tooltip?: string
  icon?: EditorIconType
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  tooltip: '',
  icon: 'columns',
})

const ColumnLayoutOptions: ColumnLayoutOption[] = [
  { label: 'Two Columns', value: ColumnLayoutEnum.TwoColumn, icon: 'two-columns' },
  { label: 'Sidebar Left', value: ColumnLayoutEnum.SidebarLeft, icon: 'sidebar-left' },
  { label: 'Sidebar Right', value: ColumnLayoutEnum.SidebarRight, icon: 'sidebar-right' },
]

const active = computed((): ColumnLayoutEnum => {
  if (props.editor?.isActive('columns')) {
    return props.editor.getAttributes('columns').layout as ColumnLayoutEnum
  }
  return ColumnLayoutEnum.TwoColumn
})

function setColumns(item: ColumnLayoutOption): void {
  if (props.editor.isActive('columns')) {
    props.editor.chain().focus().setLayout(item.value).run()
  } else {
    const chain = props.editor
      .chain()
      .focus()
      .setColumns()
      .focus(props.editor.state.selection.head - 1)
    if (item.value !== ColumnLayoutEnum.TwoColumn) {
      chain.setLayout(item.value)
    }
    chain.run()
  }
}

function removeColumns(): void {
  props.editor.chain().focus().removeColumns().run()
}

function renderLayoutIcon(icon: 'two-columns' | 'sidebar-left' | 'sidebar-right') {
  switch (icon) {
    case 'two-columns':
      return `
        <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" class="h-full w-full">
          <rect x="4" y="4" width="18" height="40" rx="2" stroke="currentColor" stroke-width="2" fill="none"/>
          <rect x="26" y="4" width="18" height="40" rx="2" stroke="currentColor" stroke-width="2" fill="none"/>
        </svg>
      `
    case 'sidebar-left':
      return `
        <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" class="h-full w-full">
          <rect x="4" y="4" width="14" height="40" rx="2" stroke="currentColor" stroke-width="2" fill="none"/>
          <rect x="22" y="4" width="22" height="40" rx="2" stroke="currentColor" stroke-width="2" fill="none"/>
        </svg>
      `
    case 'sidebar-right':
      return `
        <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" class="h-full w-full">
          <rect x="4" y="4" width="22" height="40" rx="2" stroke="currentColor" stroke-width="2" fill="none"/>
          <rect x="30" y="4" width="14" height="40" rx="2" stroke="currentColor" stroke-width="2" fill="none"/>
        </svg>
      `
  }
}
</script>

<template>
  <ActionDropdownButtonSplit
    :icon="icon"
    :action="setColumns"
    :disabled="disabled"
    :tooltip="tooltip"
    class="flex w-full min-w-4 flex-col gap-1"
  >
    <div class="grid grid-cols-3 gap-1">
      <TooltipProvider>
        <Tooltip v-for="item in ColumnLayoutOptions" :key="item.value" :delay-duration="0">
          <TooltipTrigger>
            <DropdownMenuItem class="p-0" @click="setColumns(item)">
              <div
                :class="[active === item.value ? 'border-accent-foreground bg-accent border' : '']"
                class="box-border flex h-[48px] w-[48px] flex-col rounded-xs border"
              >
                <div class="flex h-full w-full items-center justify-center p-2" v-html="renderLayoutIcon(item.icon)" />
              </div>
            </DropdownMenuItem>
          </TooltipTrigger>
          <TooltipContent side="bottom" class="bg-primary text-primary-foreground text-xs">
            {{ item.label }}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
    <DropdownMenuSeparator v-if="editor.isActive('columns')" />
    <DropdownMenuItem
      v-if="editor.isActive('columns')"
      class="text-destructive focus:text-destructive cursor-pointer"
      @click="removeColumns"
    >
      Remove Columns
    </DropdownMenuItem>
  </ActionDropdownButtonSplit>
</template>
